import { styled } from "@mui/material/styles";
import { useContext, type ReactNode } from "react";
import { ArchivedProposalNegotiationContext } from "work/proposals/_views/archive/archived-proposal-negotiation-context";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import { proposalArchivedFieldRestoreLockReason } from "work/proposals/negotiation/proposal-field-restore";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";

/** One field's read-only change history on the archived proposal page. */
export function ArchivedFieldHistoryButton(props: Readonly<{ field: ProposalField; toolTip?: string }>) {
  const negotiation = useContext(ArchivedProposalNegotiationContext);
  return (
    <ProposalFieldHistoryButton
      field={props.field}
      negotiationId={negotiation?.negotiationId}
      viewerScope={negotiation?.viewerScope}
      toolTip={props.toolTip}
      readOnlyReason={proposalArchivedFieldRestoreLockReason}
    />
  );
}

const FieldRow = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(0.5),
  width: "100%",
  // The field takes what the row has left; the button takes only its own width.
  "& > :first-of-type": {
    flex: 1,
  },
}));

/** Holds a history button's place beside a field that has no history, so the column stays aligned. */
const HistoryButtonSpace = styled("span")(({ theme }) => ({
  flexShrink: 0,
  // A medium `IconButton`: a 24px icon and 8px of padding on each side.
  width: theme.spacing(5),
}));

/**
 * A disabled field with its history button beside it, not inside it: an adornment inside a disabled
 * input sits in a control that reads as switched off. Without `field`, the field has no history of
 * its own and the button's place is kept empty — unless no field on the page has a history, when no
 * place is kept for one.
 */
export function ArchivedFieldWithHistory(props: Readonly<{ field?: ProposalField; children: ReactNode }>) {
  const negotiation = useContext(ArchivedProposalNegotiationContext);
  let history: ReactNode = null;
  if (negotiation) history = props.field ? <ArchivedFieldHistoryButton field={props.field} /> : <HistoryButtonSpace />;
  return (
    <FieldRow>
      {props.children}
      {history}
    </FieldRow>
  );
}

const ControlRow = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  gap: theme.spacing(0.5),
}));

/** A control that keeps its own width — a switch — with its history beside it. */
export function ArchivedControlWithHistory(
  props: Readonly<{ field: ProposalField; toolTip?: string; children: ReactNode }>,
) {
  return (
    <ControlRow>
      {props.children}
      <ArchivedFieldHistoryButton field={props.field} toolTip={props.toolTip} />
    </ControlRow>
  );
}
